import { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { getUserColor } from '@/core/lib/colors';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { useMemberSummary } from '../../hooks/useMemberSummary';
import type { OrgMemberNodeData } from '../../hooks/useOrgChartData';
import { MemberSummaryPopover } from './MemberSummaryPopover';

interface OrgMemberNodeProps {
  data: OrgMemberNodeData;
}

export function OrgMemberNode({ data }: OrgMemberNodeProps) {
  const {
    memberId,
    organizationMemberId,
    displayName,
    avatarUrl,
    jobTitle,
    completedTasksCount,
    totalTasksCount,
    dimmed,
  } = data;
  const color = getUserColor(memberId);
  const progress =
    totalTasksCount > 0 ? Math.round((completedTasksCount / totalTasksCount) * 100) : 0;
  const { t } = useTranslation('organizationUnits');

  const [open, setOpen] = useState(false);
  // The org-chart "Proyectos" tab can return a project-scoped id in `memberId` —
  // the summary endpoint only accepts the tenant-level id.
  const { data: summary, isLoading } = useMemberSummary(organizationMemberId ?? '', open);

  return (
    <div
      className={cn(
        'relative flex w-45 flex-col rounded-xl border bg-card shadow-sm transition-opacity',
        dimmed && 'opacity-30',
      )}
      style={{ pointerEvents: 'all' }}
      data-testid={`org-chart-member-node-${memberId}`}
    >
      <Handle type="target" position={Position.Top} className="border-border! bg-muted!" />

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex flex-col items-center gap-2 rounded-xl px-3 py-3 transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            data-testid={`org-chart-member-detail-${memberId}`}
          >
            <UserAvatar
              name={displayName ?? ''}
              color={color}
              src={avatarUrl ?? undefined}
              size="lg"
            />

            <div className="w-full text-center">
              <p className="truncate text-sm font-semibold text-foreground">{displayName}</p>
              <p className="truncate text-xs capitalize text-muted-foreground">{jobTitle}</p>
            </div>

            <div className="w-full">
              <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                <span>
                  {t('orgChart.memberNode.progressLabel', {
                    completed: completedTasksCount,
                    total: totalTasksCount,
                  })}
                </span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-1.5 rounded-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-105 p-0" align="start" side="right" sideOffset={8}>
          <MemberSummaryPopover
            displayName={displayName}
            avatarUrl={avatarUrl}
            memberId={memberId}
            summary={summary}
            isLoading={isLoading}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
